import { useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { FIREBASE_ENABLED, requestFcmToken, onForegroundPush } from "@/lib/firebase";
import { toast } from "sonner";

const STORAGE_KEY = "afr_fcm_token";

function detectDisplayMode() {
  try {
    if (typeof window === "undefined") return "ssr";
    // iOS-specific flag for "Add to Home Screen"
    if (window.navigator.standalone) return "ios-standalone";
    // Android Chrome installed PWA
    if (window.matchMedia("(display-mode: standalone)").matches) return "standalone";
    if (window.matchMedia("(display-mode: minimal-ui)").matches) return "minimal-ui";
    if (window.matchMedia("(display-mode: fullscreen)").matches) return "fullscreen";
    return "browser";
  } catch {
    return "unknown";
  }
}

function setStatus(patch) {
  try {
    const prev = JSON.parse(localStorage.getItem("afr_push_status") || "{}");
    const next = { ...prev, ...patch, updatedAt: Date.now() };
    localStorage.setItem("afr_push_status", JSON.stringify(next));
    window.dispatchEvent(new CustomEvent("afr-push-status", { detail: next }));
  } catch { /* noop */ }
}

/**
 * Push pipeline manager.
 *
 * Critical Android behaviour: the installed PWA and Chrome share the SAME
 * service-worker registration on the same origin, so they share the SAME FCM
 * token. The "owner" of the live subscription is whichever client most
 * recently posted a token to the backend. Therefore we:
 *
 *   1. Re-acquire AND re-register the token on EVERY mount (idempotent on
 *      the backend; `$addToSet` keeps it cheap).
 *   2. Send `displayMode` + `installed` flags so the backend can record which
 *      client most recently "owned" the subscription.
 *   3. Drop the cached token if it was registered by a different user.
 */
export default function PushNotifications() {
  const { user } = useAuth();

  useEffect(() => {
    if (!user || user === false) return;
    const wantsPush =
      (user.role === "mechanic" && user.isApproved) ||
      user.role === "admin" ||
      user.role === "client";
    if (!wantsPush) return;

    const displayMode = detectDisplayMode();
    const installed = displayMode === "standalone" || displayMode === "ios-standalone";

    setStatus({
      fcmSupported: FIREBASE_ENABLED,
      permission: typeof Notification !== "undefined" ? Notification.permission : "unsupported",
      role: user.role,
      displayMode,
      installed,
    });

    if (!FIREBASE_ENABLED) {
      console.warn("[Push] Firebase env vars missing — skipping token acquisition");
      return;
    }

    // If the cached token belongs to a different user, drop it.
    try {
      const cachedUid = localStorage.getItem("afr_fcm_uid");
      if (cachedUid && cachedUid !== user.id) {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem("afr_fcm_displaymode");
      }
    } catch { /* noop */ }

    let cancelled = false;
    (async () => {
      try {
        console.log("[Push] mode=%s installed=%s — requesting FCM token", displayMode, installed);
        const token = await requestFcmToken();
        if (cancelled) return;
        if (!token) {
          console.warn("[Push] No FCM token (denied / unsupported / SW failed)");
          setStatus({
            permission: typeof Notification !== "undefined" ? Notification.permission : "unsupported",
            tokenAcquired: false,
            tokenRegistered: false,
          });
          return;
        }
        console.log("[Push] token acquired (len=%d)", token.length);
        setStatus({ permission: "granted", tokenAcquired: true });

        // ALWAYS POST — even if the token matches the cache — so the backend
        // re-records `displayMode` and treats this client as the live owner.
        // This is the line that makes the installed PWA the primary push
        // subscriber when it launches.
        const cachedDM = localStorage.getItem("afr_fcm_displaymode");
        const cachedTok = localStorage.getItem(STORAGE_KEY);
        const shouldPost = cachedTok !== token || cachedDM !== displayMode;
        if (!shouldPost) {
          console.log("[Push] token + displayMode already synced, skipping POST");
          setStatus({ tokenRegistered: true });
          return;
        }
        const { data } = await api.post("/notifications/register-token", {
          token,
          platform: "web",
          displayMode,
          installed,
        });
        localStorage.setItem(STORAGE_KEY, token);
        localStorage.setItem("afr_fcm_uid", user.id);
        localStorage.setItem("afr_fcm_displaymode", displayMode);
        console.log("[Push] backend register-token →", data);
        setStatus({ tokenRegistered: true, welcomeSent: !!data.welcomeSent });
      } catch (e) {
        console.warn("[Push] register-token flow failed", e?.response?.data || e?.message);
        setStatus({ tokenRegistered: false, lastError: e?.response?.data?.detail || e?.message });
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id, user?.role, user?.isApproved]);

  useEffect(() => {
    if (!FIREBASE_ENABLED) return;
    const ICON = "https://customer-assets.emergentagent.com/job_mechanic-connect-71/artifacts/muj6518c_1000335256.png";
    const unsub = onForegroundPush(async (payload) => {
      try {
        const data = payload?.data || {};
        // Silent admin events → trigger realtime dashboard refresh, never display
        if (data.adminEvent === "1" || data.silent === "1") {
          window.dispatchEvent(new CustomEvent("afr-admin-event", { detail: data }));
          return;
        }
        const title = data.title || payload?.notification?.title || "AutoFix Rescue";
        const body = data.body || payload?.notification?.body || "New update";

        // In-app toast (visible while tab is focused)
        toast(title, { description: body, duration: 6000 });

        // Also draw a real system notification through the active SW so it
        // lands in the Android notification tray and is visible after the
        // user switches apps. The SW dedupes via the `tag`.
        if ("serviceWorker" in navigator) {
          const reg = await navigator.serviceWorker.getRegistration("/firebase-messaging-sw.js")
            || (await navigator.serviceWorker.ready);
          if (reg && typeof Notification !== "undefined" && Notification.permission === "granted") {
            await reg.showNotification(title, {
              body,
              icon: ICON,
              badge: ICON,
              tag: data.requestId || data.tag || "autofix-job",
              renotify: true,
              requireInteraction: data.requireInteraction !== "0",
              vibrate: [200, 100, 200],
              data,
            });
          }
        }
      } catch (e) {
        console.warn("[Push] foreground render failed", e?.message);
      }
    });
    return () => { try { unsub(); } catch { /* noop */ } };
  }, []);

  return null;
}
